import { useEffect, useRef, useState } from "react";

const PieChart = ({ data, colors, labels }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const slicesRef = useRef([]);
  const [tip, setTip] = useState(null);
  const totalItems = (data || []).filter((v) => Number(v) > 0).length;
  const totalCount = (data || []).reduce((a, v) => a + (Number(v) || 0), 0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) / 2 - 18;

    const total = (data || []).reduce((a, v) => a + (Number(v) || 0), 0);

    const defaultColors = [
      "#4F46E5",
      "#22C55E",
      "#0EA5E9",
      "#F97316",
      "#EC4899",
      "#A855F7",
      "#14B8A6",
      "#64748B",
    ];
    const palette = colors && colors.length ? colors : defaultColors;

    ctx.clearRect(0, 0, width, height);

    // No data
    if (!total) {
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = "#e5e7eb";
      ctx.fill();
      ctx.fillStyle = "#6b7280";
      ctx.font = "16px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("No Data", cx, cy);
      slicesRef.current = [];
      return;
    }

    const hoverIndex = tip?.index ?? -1;
    const slices = [];
    let startAngle = 0;

    // 라벨 표시 기준(조절 가능)
    const minAngleForLabel = 0.35; // 약 20도
    const textRadius = radius * 0.62;
    const textPadding = 12;

    data.forEach((raw, index) => {
      const value = Number(raw) || 0;
      if (value <= 0) return;

      const ratio = value / total;
      const sliceAngle = ratio * Math.PI * 2;

      const endAngle = startAngle + sliceAngle;
      const color = palette[index % palette.length];
      const midAngle = startAngle + sliceAngle / 2;

      // slice 저장(호버 판별용)
      slices.push({
        index,
        startAngle,
        endAngle,
        value,
        ratio,
        label: labels?.[index] ?? "",
        color,
      });

      // 파이 조각
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();

      // 테두리
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.stroke();

      // 호버 하이라이트(살짝 진하게)
      if (index === hoverIndex) {
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = "rgba(0,0,0,0.08)";
        ctx.fill();
      }

      // 큰 조각만 라벨 표시: 각도 + 텍스트폭 기준
      const label = labels?.[index] ?? "";
      const text = `${label} (${value})`;

      ctx.font = "13px Arial";
      const textW = ctx.measureText(text).width;
      const arcLen = sliceAngle * textRadius; // 해당 반지름에서의 호 길이

      const canDrawLabel = sliceAngle >= minAngleForLabel && arcLen >= textW + textPadding;

      if (canDrawLabel) {
        const tx = cx + Math.cos(midAngle) * textRadius;
        const ty = cy + Math.sin(midAngle) * textRadius;

        // 대비용: 글자 배경 살짝
        ctx.fillStyle = "rgba(255,255,255,0.65)";
        const padX = 6;
        const padY = 4;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillRect(tx - textW / 2 - padX, ty - 7 - padY, textW + padX * 2, 14 + padY * 2);

        ctx.fillStyle = "#111827";
        ctx.fillText(text, tx, ty);
      }

      startAngle = endAngle;
    });

    slicesRef.current = slices;
  }, [data, colors, labels, tip?.index]);

  const pickSlice = (e) => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return null;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const dx = x - cx;
    const dy = y - cy;

    const r = Math.sqrt(dx * dx + dy * dy);
    const radius = Math.min(canvas.width, canvas.height) / 2 - 18;
    if (r > radius) return null;

    let ang = Math.atan2(dy, dx);
    if (ang < 0) ang += Math.PI * 2;

    const slices = slicesRef.current || [];
    const hit = slices.find((s) => ang >= s.startAngle && ang < s.endAngle);
    if (!hit) return null;

    return {
      ...hit,
      x: e.clientX,
      y: e.clientY,
    };
  };

  const onMove = (e) => {
    const hit = pickSlice(e);
    if (!hit) {
      setTip(null);
      return;
    }

    setTip({
      index: hit.index,
      label: hit.label,
      value: hit.value,
      percent: Math.round(hit.ratio * 1000) / 10,
      x: hit.x,
      y: hit.y,
    });
  };

  const onLeave = () => setTip(null);

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <canvas
        ref={canvasRef}
        width={270}
        height={270}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ borderRadius: 12 }}
      />
      {tip && (
        <div
          style={{
            position: "fixed",
            left: tip.x + 12,
            top: tip.y + 12,
            background: "rgba(17,24,39,0.92)",
            color: "#fff",
            padding: "8px 10px",
            borderRadius: 10,
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 9999,
            maxWidth: 220,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <div style={{ fontWeight: 800 }}>
            {tip.label || "(이름 없음)"}
          </div>
          <div>
            {tip.value} ({tip.percent}%)
          </div>
        </div>
      )}

      <div style={{ marginTop: 5, fontSize: 12, color: "#374151", textAlign: "center" }}>
        총 {totalItems}품목 {totalCount}개
      </div>
    </div>
  );
};

export default PieChart;
